import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { useApi } from '../../lib/ApiContext';
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";

const RouteGuard = ({ children, requiresOrganization = false, requiresTemplate = false }) => {
    const [loading, setLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const [error, setError] = useState(null);

    const { user } = useAuth();
    const api = useApi();
    const navigate = useNavigate();
    const params = useParams();

    useEffect(() => {
        const checkAccess = async () => {
            try {
                // If user is super admin, they have access to everything
                if (user?.is_super_admin) {
                    setHasAccess(true);
                    setLoading(false);
                    return;
                }

                // Check organization access if required
                if (requiresOrganization) {
                    const orgPublicId = params.publicId || params.orgPublicId; // Handle both /organizations/:publicId/edit and /organizations/:orgPublicId/templates/new

                    if (orgPublicId) {
                        try {
                            // Try to fetch the organization by public_id - if successful, user has access
                            await api.organizations.getByPublicId(orgPublicId);
                            setHasAccess(true);
                        } catch (err) {
                            if (err.message.includes('404') || err.message.includes('access denied')) {
                                setHasAccess(false);
                                setError('You do not have access to this organization.');
                            } else {
                                throw err;
                            }
                        }
                    } else {
                        setHasAccess(false);
                        setError('Organization ID not found in route.');
                    }
                }

                // Check template access if required
                else if (requiresTemplate && params.templatePublicId) {
                    try {
                        // Try to fetch the template by public_id - if successful, user has access
                        await api.templates.getByPublicId(params.templatePublicId);
                        setHasAccess(true);
                    } catch (err) {
                        if (err.message.includes('404') || err.message.includes('access denied')) {
                            setHasAccess(false);
                            setError('You do not have access to this template.');
                        } else {
                            throw err;
                        }
                    }
                }

                // If no specific access check is required, allow access
                else {
                    setHasAccess(true);
                }

                setLoading(false);
            } catch (err) {
                console.error('Error checking route access:', err);
                setError('Failed to verify access permissions.');
                setHasAccess(false);
                setLoading(false);
            }
        };

        if (user) {
            checkAccess();
        } else {
            // User not authenticated, redirect to login will be handled by auth system
            setLoading(false);
        }
    }, [user, params.publicId, params.orgPublicId, params.templatePublicId, requiresOrganization, requiresTemplate, api, navigate]);

    if (loading) {
        return (
            <div className="container mx-auto p-4">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900" role="status">
                        <span className="sr-only">Checking access permissions...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="container mx-auto p-4">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Access Denied</AlertTitle>
                    <AlertDescription>
                        {error || 'You do not have permission to access this resource.'}
                        <br />
                        <button
                            onClick={() => navigate(-1)}
                            className="mt-2 text-blue-600 hover:underline"
                        >
                            Go back
                        </button>
                        {' or '}
                        <button
                            onClick={() => navigate('/organizations')}
                            className="text-blue-600 hover:underline"
                        >
                            return to organizations
                        </button>
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return children;
};

export default RouteGuard; 